import { ImgID } from "../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { formatShortDuration } from "../../../common/common_funcs.js";

export class AdventureHeader
{
  private readonly ctx: CanvasRenderingContext2D;

  private readonly HEADER_DESIGN_H = 326;
  private readonly TIMER_DESIGN_W = 309;
  private readonly TIMER_DESIGN_H = 88;
  private readonly INFO_BTN_SIZE = 147;
  private readonly CLOSE_BTN_SIZE = 110;
  private readonly SIDE_PADDING = 30;
  private readonly ICON_CENTER_Y = 120;
  private readonly INFO_ICON_CENTER_Y = 100;
  private readonly TIMER_Y = 232;
  private readonly TITLE_Y = 140;
  private readonly TIMER_TEXT_X = 100;
  private readonly TIMER_TEXT_RIGHT_PADDING = 35;
  private readonly TIMER_PILL_TOP = 12;
  private readonly TIMER_PILL_H = 60;
  private timer_text: UITextElement | null = null;
  private period_end_ms = 0;
  private timer_refreshed_at = 0;

  constructor( ctx: CanvasRenderingContext2D )
  {
    this.ctx = ctx;
  }

  build( screen: UIElement, screen_w: number, period_end: string ): void
  {
    const parsed_period_end = Date.parse( period_end );
    this.period_end_ms = Number.isFinite( parsed_period_end ) ? parsed_period_end : 0;
    const header_h = LayoutScaler.heightFromDesign( this.HEADER_DESIGN_H );
    const header = new UIImage( this.ctx, 0, 0, screen_w, header_h, ImgID.UI_AdventureHeaderBg );
    screen.addChild( header );

    this.buildTitle( header, screen_w );
    this.buildInfoButton( header );
    this.buildCloseButton( header, screen_w );
    this.buildTimer( header, screen_w );
    this.update( performance.now(), true );
  }

  private buildTitle( parent: UIElement, content_w: number ): void
  {
    const x = 0;
    const y = LayoutScaler.yFromDesign( this.TITLE_Y );
    const h = LayoutScaler.heightFromDesign( 85 );

    const title = new UITextElement( this.ctx, x, y + LayoutScaler.yFromDesign( 2 ), content_w, h, {
      localization_key: "ADVENTURE",
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
      outline_color: ColorScheme.adventure_header_title_outline,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent_30
    } );

    const title_1 = new UITextElement( this.ctx, x, y, content_w, h, {
      localization_key: "ADVENTURE",
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent
    } );

    parent.addChild( title );
    parent.addChild( title_1 );
  }

  private buildInfoButton( parent: UIElement ): void
  {
    const size = LayoutScaler.widthFromDesign( this.INFO_BTN_SIZE );
    const x = LayoutScaler.xFromDesign( this.SIDE_PADDING );
    const y = LayoutScaler.yFromDesign( this.INFO_ICON_CENTER_Y );

    const button = new UIImage( this.ctx, x, y, size, size, ImgID.UI_LeaderboardInfoButton, {}, true );
    button.setOnClick( () => OverlayFactory.openOverlay( OverlayId.ADVENTURE_INFO_OVERLAY ) );

    parent.addChild( button );
  }

  private buildCloseButton( parent: UIElement, screen_w: number ): void
  {
    const size = LayoutScaler.widthFromDesign( this.CLOSE_BTN_SIZE );
    const x = screen_w - size - LayoutScaler.xFromDesign( this.SIDE_PADDING );
    const y = LayoutScaler.yFromDesign( this.ICON_CENTER_Y );

    const button = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    button.setOnClick( () => ScreenManager.showScreen( Screens.MENU ) );

    parent.addChild( button );
  }

  private buildTimer( parent: UIElement, screen_w: number ): void
  {
    const w = LayoutScaler.widthFromDesign( this.TIMER_DESIGN_W );
    const h = LayoutScaler.heightFromDesign( this.TIMER_DESIGN_H );
    const x = screen_w / 2 - w / 2;
    const y = LayoutScaler.yFromDesign( this.TIMER_Y );

    const timer = new UIImage( this.ctx, x, y, w, h, ImgID.UI_AdventureHeaderTimer );
    parent.addChild( timer );

    const text_x = LayoutScaler.widthFromDesign( this.TIMER_TEXT_X );
    const text_w = w - text_x - LayoutScaler.widthFromDesign( this.TIMER_TEXT_RIGHT_PADDING );
    const text_y = LayoutScaler.heightFromDesign( this.TIMER_PILL_TOP );
    const text_h = LayoutScaler.heightFromDesign( this.TIMER_PILL_H );

    const text = new UITextElement( this.ctx, text_x, text_y, text_w, text_h, {
      text: "",
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      center_on_cap_height: true,
      ...TextStyleScheme.timerShadow(),
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
    } );

    timer.addChild( text );
    this.timer_text = text;
  }

  public update( now: number, force = false ): void
  {
    if ( !this.timer_text || ( !force && now - this.timer_refreshed_at < 1000 ) )
      return;

    this.timer_refreshed_at = now;
    const remaining_ms = this.period_end_ms > 0
      ? Math.max( 0, this.period_end_ms - Date.now() )
      : 0;
    this.timer_text.setText( formatShortDuration( remaining_ms ) );
  }
}
